import { useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { SOCKET_IO_URL } from "../lib/utils";

const NAMESPACE = "/tickets";
export const TICKET_ACTIVITY_EVENT = "ticket:activity";

export function broadcastTicketActivity(ticket, event = "updated") {
  try {
    window.dispatchEvent(
      new CustomEvent(TICKET_ACTIVITY_EVENT, { detail: { ticket, event } }),
    );
  } catch {
    /* ignore */
  }
}

export default function useTicketRealtime({
  token,
  ticketId,
  enabled = true,
  onTicket,
}) {
  const socketRef = useRef(null);
  const onTicketRef = useRef(onTicket);
  const ticketIdRef = useRef(ticketId);

  useEffect(() => {
    onTicketRef.current = onTicket;
  }, [onTicket]);

  useEffect(() => {
    ticketIdRef.current = ticketId;
    const socket = socketRef.current;
    if (socket?.connected && ticketId) {
      socket.emit("ticket:join", { ticket_id: String(ticketId) });
    }
  }, [ticketId]);

  useEffect(() => {
    if (!enabled || !token) return undefined;

    const socket = io(`${SOCKET_IO_URL}${NAMESPACE}`, {
      auth: { token },
      query: { token },
      path: "/api/socket.io",
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      if (ticketIdRef.current) {
        socket.emit("ticket:join", { ticket_id: String(ticketIdRef.current) });
      }
    });

    socket.on("ticket:updated", (payload) => {
      const ticket = payload?.ticket;
      if (!ticket) return;
      const event = payload?.event || "updated";
      broadcastTicketActivity(ticket, event);
      onTicketRef.current?.(ticket, event);
    });

    return () => {
      socket.removeAllListeners();
      if (socket.connected) socket.disconnect();
      else socket.once("connect", () => socket.disconnect());
      socketRef.current = null;
    };
  }, [enabled, token]);
}
